import type { TalkKind, TalkMessage } from '@/services/talk-it-over'

import { formatDateTime } from '@/utils/dateUtils'

/**
 * Open in Claude Code names one thing in the Claude area as `<kind>:<id>`. The fleet
 * (tattle-claude-fleet src/continue-ref.ts), the workspace launcher and handler (bin/continue_ref.py) and the
 * plugins (tattle_core/continue_link.py) check the same patterns: change all four together.
 */
export type ContinueKind = 'talk' | 'ask' | 'run' | 'ticket' | 'pr' | 'build' | 'test' | 'confluence' | 'backmerge' | 'job'

export interface ContinueContext {
  kind: ContinueKind
  id: string
}

const KEY = '[A-Z][A-Z0-9]{1,9}-\\d{1,7}'
/** `<repo>#<n>`: a Bitbucket build. A pull request may add `/<comment id>`, one review finding's thread. */
const REPO_NUMBER = /^[A-Za-z0-9._-]{1,80}#\d{1,7}$/
const PR_FINDING = /^[A-Za-z0-9._-]{1,80}#\d{1,7}(?:\/\d{1,12})?$/
const ID_PATTERNS: Record<ContinueKind, RegExp> = {
  talk: new RegExp(`^(?:${KEY}(?:\\.\\d{1,20})?|(?:decision|pr|cancelled|run|wish)/[A-Za-z0-9._~,:-]{1,200})$`),
  ask: new RegExp(`^(?:[a-z][a-z-]{1,30}(?:\\.[a-z]{2,20})?|ticket\\.${KEY})$`),
  run: /^[A-Za-z0-9._:-]{1,120}$/,
  ticket: new RegExp(`^${KEY}$`),
  pr: PR_FINDING,
  build: REPO_NUMBER,
  test: /^tests\/(?!.*\.\.)[A-Za-z0-9._/-]{1,200}\.spec\.ts(?::\d{1,6})?$/,
  /** A Confluence page, by its id. */
  confluence: /^\d{1,20}$/,
  /** `<repo>#<branch>`: a release repo's main or release branch with changes develop lacks. */
  backmerge: /^[A-Za-z0-9._-]{1,80}#(?!.*\.\.)[A-Za-z0-9][A-Za-z0-9._/-]{0,119}$/,
  /** A row on Fleet → Overview → Fleet jobs: a scheduled job, an inbox, a queue or a sync (a sync's id holds a colon). */
  job: /^[a-z0-9][a-z0-9._:-]{0,79}$/
}

/** A context whose id matches its kind, or null: a malformed one never becomes a link. */
export function continueContext(kind: ContinueKind, id: string | number | null | undefined): ContinueContext | null {
  const text = id === null || id === undefined ? '' : String(id)
  return ID_PATTERNS[kind].test(text) ? { kind, id: text } : null
}

/** A Bitbucket build as Claude names it, `<repo>#<build>`; null without a build number. */
export const buildContext = (repo: string, build: number | string | null | undefined): ContinueContext | null =>
  continueContext('build', `${repo}#${build ?? ''}`)

export const contextText = (context: ContinueContext): string => `${context.kind}:${context.id}`

/** The longest `<kind>:<id>` text any copy of the format reads. */
const MAX_CONTEXT_LENGTH = 260

/** `<kind>:<id>` read back, split at the first colon since a run id may hold more; null unless it checks out. */
export function parseContinueContext(text: string): ContinueContext | null {
  if (text.length > MAX_CONTEXT_LENGTH) return null
  const at = text.indexOf(':')
  const kind = text.slice(0, at) as ContinueKind
  return at > 0 && Object.prototype.hasOwnProperty.call(ID_PATTERNS, kind) ? continueContext(kind, text.slice(at + 1)) : null
}

/** The tool the session calls first, on the Tattle connector. */
const BRIEFING_TOOL = 'tattle_continue_briefing'

/** The one-click link the workspace's handler opens a terminal for (bin/install-continue-handler). */
export const terminalUrl = (context: ContinueContext): string =>
  `tattle-claude://continue/${context.kind}/${encodeURIComponent(context.id)}`

/** The command to run from the workspace folder. */
export function launcherCommand(context: ContinueContext, shell: 'posix' | 'windows' = 'posix'): string {
  const text = contextText(context)
  return shell === 'windows'
    ? `.\\bin\\claude-multi.ps1 -PickUp '${text}'`
    : `./bin/claude-multi.sh --pick-up '${text}'`
}

/** Claude Desktop's first message: it names the context only, never the conversation, since a link lands in history. */
export function desktopPrompt(context: ContinueContext): string {
  const text = contextText(context)
  return (
    `Pick up ${text} from ADX: call the Tattle connector's ${BRIEFING_TOOL} tool with context "${text}", ` +
    'then carry on from its briefing. Ask me before posting, commenting or changing anything outside this machine.'
  )
}

/** Claude Desktop's own link: a Code session in `folder`, with the first message typed but not sent. Spaces are `%20`, since only a form decoder reads `+` as a space. */
export const desktopUrl = (context: ContinueContext, folder: string): string =>
  `claude://code/new?${new URLSearchParams({ folder, q: desktopPrompt(context) }).toString().replace(/\+/g, '%20')}`

/** What an Ask Claude drawer picks up: its own conversation, or, for Work it with Claude and an Ask Claude about a
 *  card, the thing it is about. */
export function talkContext(kind: TalkKind, id: string): ContinueContext | null {
  if (kind === 'page') return continueContext('ask', id)
  if (kind === 'continue') return parseContinueContext(id)
  if (kind === 'decision') return continueContext('talk', id)
  if (kind !== 'action') return continueContext('talk', `${kind}/${id}`)
  if (id.startsWith('ticket.')) return continueContext('ticket', id.slice('ticket.'.length))
  if (id.startsWith('pr.')) return continueContext('pr', id.slice('pr.'.length).replace('~', '#'))
  return null
}

/** The message the briefing tool adds to a conversation when someone picks it up (tattle-claude-fleet CONTINUED_TEXT). */
export const CONTINUED_TEXT = 'Continued in Claude Code.'

export const isContinuedEvent = (message: TalkMessage): boolean =>
  message.role === 'event' && message.text === CONTINUED_TEXT

/** "Opened in Claude Code by Pat Lee, Oct 20, 3:00 PM", or '' when nobody has picked it up. */
export function continuedText(messages: TalkMessage[], format: (at: string) => string = formatDateTime): string {
  const last = [...messages].reverse().find(isContinuedEvent)
  return last ? `Opened in Claude Code by ${last.name}, ${format(last.at)}` : ''
}
